import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { onTourImageError } from "@/lib/imageFallback";

// Hero photos provided by the client. Each entry carries both desktop (2400)
// and mobile (1200) sources; the browser picks via srcset. Files live in
// /public/brand/hero so they ship with the frontend bundle — not via R2 — so
// first paint never blocks on the CDN.
const heroImages = [
  { src: "/brand/hero/i4-2400.jpg", mobile: "/brand/hero/i4-1200.jpg", altKey: "hero.alt1" },
  { src: "/brand/hero/i1-2400.jpg", mobile: "/brand/hero/i1-1200.jpg", altKey: "hero.alt2" },
  { src: "/brand/hero/i5-2400.jpg", mobile: "/brand/hero/i5-1200.jpg", altKey: "hero.alt3" },
];

export default function HeroSection() {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);
  const [destOpen, setDestOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [selectedDest, setSelectedDest] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [date, setDate] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  const destinations = [
    { label: t("destinations.laPaz"), value: "la-paz" },
    { label: t("destinations.losCabos"), value: "los-cabos" },
    { label: t("destinations.caboSanLucas"), value: "cabo-san-lucas" },
  ];

  // Values match the canonical names the /tours page uses to map to the backend enum.
  // Do not change casing or accents without updating tours/page.tsx::categoryByName.
  const categories = [
    { label: t("categories.adventure"), value: "Aventura" },
    { label: t("categories.cultural"), value: "Cultural" },
    { label: t("categories.gastronomic"), value: "Gastronómico" },
    { label: t("categories.transport"), value: "Transporte" },
    { label: t("categories.housing"), value: "Renta de Casas" },
    { label: t("categories.fishing", { defaultValue: "Pesca" }), value: "Pesca" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedDest) params.set("destination", selectedDest);
    if (selectedCat) params.set("category", selectedCat);
    if (date) params.set("date", date);
    if (adults) params.set("adults", String(adults));
    if (children) params.set("children", String(children));
    navigate(`/tours?${params.toString()}`);
  }, [selectedDest, selectedCat, date, adults, children, navigate]);

  return (
    <section className="relative w-full h-[660px] md:h-[680px] lg:h-[760px] overflow-hidden">
      {heroImages.map((img, i) => (
        <div
          key={img.src}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === currentImage ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={img.src}
            srcSet={`${img.mobile} 1200w, ${img.src} 2400w`}
            sizes="100vw"
            alt={t(img.altKey)}
            onError={onTourImageError}
            // First slide is critical for LCP, preload eagerly; rest lazy-load.
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />

      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 md:px-8 lg:px-12 pt-16">
        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white text-center max-w-4xl leading-tight">
          {t("hero.title")}
        </h1>
        <p className="mt-4 md:mt-6 text-base md:text-xl text-white/90 text-center max-w-2xl">
          {t("hero.subtitle")}
        </p>

        <div className="mt-8 md:mt-12 w-full max-w-5xl bg-white/10 backdrop-blur-md rounded-2xl p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-2 md:gap-3">
            <div className="relative flex-1">
              <button
                onClick={() => { setDestOpen(!destOpen); setCatOpen(false); }}
                className="w-full flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 text-left hover:bg-white/95 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center text-ocean">
                  <i className="ri-map-pin-line" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">{t("hero.searchPlaceholder")}</p>
                  <p className="text-sm font-medium text-charcoal truncate">
                    {selectedDest
                      ? destinations.find((d) => d.value === selectedDest)?.label
                      : t("hero.allDestinations")}
                  </p>
                </div>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${destOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {destOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20">
                  <button
                    onClick={() => { setSelectedDest(""); setDestOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                  >
                    {t("hero.allDestinations")}
                  </button>
                  {destinations.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => { setSelectedDest(d.value); setDestOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative flex-1">
              <button
                onClick={() => { setCatOpen(!catOpen); setDestOpen(false); }}
                className="w-full flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 text-left hover:bg-white/95 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center text-ocean">
                  <i className="ri-apps-line" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">{t("hero.categoryLabel")}</p>
                  <p className="text-sm font-medium text-charcoal truncate">
                    {selectedCat
                      ? categories.find((c) => c.value === selectedCat)?.label
                      : t("hero.allCategories")}
                  </p>
                </div>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20">
                  <button
                    onClick={() => { setSelectedCat(""); setCatOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                  >
                    {t("hero.allCategories")}
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => { setSelectedCat(c.value); setCatOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 flex-1">
              <div className="w-5 h-5 flex items-center justify-center text-ocean">
                <i className="ri-calendar-line" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">{t("hero.dateLabel")}</p>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full text-sm font-medium text-charcoal bg-transparent outline-none cursor-pointer"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 flex-1">
              <div className="w-5 h-5 flex items-center justify-center text-ocean">
                <i className="ri-user-line" />
              </div>
              <div className="flex-1 flex items-center gap-4">
                <div>
                  <p className="text-xs text-gray-500">{t("hero.adultsLabel")}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label={t("hero.adultsMinus")}
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <i className="ri-subtract-line text-xs" />
                    </button>
                    <span className="text-sm font-medium text-charcoal w-4 text-center">{adults}</span>
                    <button
                      type="button"
                      aria-label={t("hero.adultsPlus")}
                      onClick={() => setAdults(adults + 1)}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <i className="ri-add-line text-xs" />
                    </button>
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-100" />
                <div>
                  <p className="text-xs text-gray-500">{t("hero.childrenLabel")}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label={t("hero.childrenMinus")}
                      onClick={() => setChildren(Math.max(0, children - 1))}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <i className="ri-subtract-line text-xs" />
                    </button>
                    <span className="text-sm font-medium text-charcoal w-4 text-center">{children}</span>
                    <button
                      type="button"
                      aria-label={t("hero.childrenPlus")}
                      onClick={() => setChildren(children + 1)}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <i className="ri-add-line text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleSearch}
              className="bg-coral hover:bg-coral/90 text-white font-semibold px-6 md:px-8 py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line" />
              </div>
              {t("hero.searchButton")}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {heroImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentImage(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === currentImage ? "w-8 bg-white" : "w-2 bg-white/50"
              }`}
              aria-label={t("hero.goToSlide", { number: i + 1 })}
            />
          ))}
        </div>
      </div>
    </section>
  );
}